import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowLeft, CheckCircle2 } from "lucide-react";

const LOGO_URL = "/logo.png";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password
      });

      // Extract token from message for demo purposes
      const message = response.data.message;
      const tokenMatch = message.match(/token (.+)$/);
      if (tokenMatch) {
        setVerificationToken(tokenMatch[1]);
      }

      toast.success("Registration successful! Please verify your email.");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (verificationToken) {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <div className="premium-card rounded-2xl p-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF66]/20 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#00FF66]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Verify Your Email</h2>
            <p className="text-[#A1A1AA] mb-6">
              We've sent a verification link to <span className="text-white">{formData.email}</span>
            </p>
            
            {/* Demo: Show verification token */}
            <div className="bg-black/50 rounded-xl p-4 mb-6">
              <p className="text-sm text-[#71717A] mb-2">For demo, use this token:</p>
              <p className="text-xs font-mono text-[#00FF66] break-all">{verificationToken}</p>
            </div>

            <Link to={`/verify-email?token=${verificationToken}`}>
              <Button 
                className="w-full bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                data-testid="verify-now-btn"
              >
                Verify Now
              </Button>
            </Link>

            <p className="mt-6 text-[#A1A1AA]">
              Already verified?{" "}
              <Link to="/login" className="text-[#00FF66] hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Credit2Cash" 
            className="h-24 mx-auto logo-glow mb-4"
            data-testid="register-logo"
          />
          <h1 className="text-2xl font-bold text-white">Create Your Account</h1>
          <p className="text-[#A1A1AA] mt-2">Start your journey to financial freedom</p>
        </div>

        {/* Register Form */}
        <div className="premium-card rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-white">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="pl-10 input-dark"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-10 input-dark"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white">Phone Number (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="pl-10 input-dark"
                  data-testid="register-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min. 6 characters"
                  className="pl-10 pr-10 input-dark"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className="pl-10 input-dark"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00FF66] text-black font-bold py-6 hover:bg-[#00FF66]/90 neon-glow-green"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Creating Account...
                </span>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#A1A1AA]">
              Already have an account?{" "}
              <Link 
                to="/login" 
                className="text-[#00FF66] hover:underline font-semibold"
                data-testid="register-login-link"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
